import { formatDate } from "@/lib/utils";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  FileText,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  MoreVertical,
  Edit,
  Trash2,
  Filter,
  Eye,
} from "lucide-react";
import ExpenseDetailsModal from "./components/ExpenseDetailsModal";
import CountUp from "react-countup";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { ReactSelect } from "@/components/ui/react-select";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageContainer } from "@/components/layout/PageContainer";
import DefaultEditor from "react-simple-wysiwyg";
import RecordNotFound from "@/core/components/RecordNotFound";


const Expenses = () => {
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const current_store = useSelector((state) => state.current_store);
  const currency = current_store?.default_currency;
  const store_id = current_store?.id || null;
  const currencySymbol = currency?.symbol || "";

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [approveRecord, setApproveRecord] = useState(null);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseData, setExpenseData] = useState({
    id: "",
    title: "",
    description: "",
    category_id: "",
    supplier_id: "",
    employee_id: "",
    store_id: "",
    amount: 0,
    tax_amount: 0,
    total_amount: 0,
    expense_date: "",
    payment_method_id: "",
    reference_number: "",
    receipt_number: "",
    notes: "",
    status: "draft",
  });

  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [supplierLabels, setSupplierLabels] = useState({});

  const [categorySearchInput, setCategorySearchInput] = useState("");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [categoryLabels, setCategoryLabels] = useState({});

  const [employeeSearchInput, setEmployeeSearchInput] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeLabels, setEmployeeLabels] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setSupplierSearchTerm(supplierSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [supplierSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCategorySearchTerm(categorySearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [categorySearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setEmployeeSearchTerm(employeeSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Queries
  const { data: expenseRes, isLoading: isExpensesLoading } = useQuery({
    queryKey: [
      "expenses",
      currentPage,
      pageSize,
      debouncedSearchText,
      store_id,
      filterCategory,
      filterEmployee,
      filterSupplier,
      filterStatus,
      filterDateFrom,
      filterDateTo,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/expenses`, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: debouncedSearchText || undefined,
          store_id: store_id || undefined,
          category_id: filterCategory || undefined,
          employee_id: filterEmployee || undefined,
          supplier_id: filterSupplier || undefined,
          status: filterStatus || undefined,
          date_from: filterDateFrom || undefined,
          date_to: filterDateTo || undefined,
          sort_by: sortBy || undefined,
          sort_order: sortOrder || undefined,
          with: [
            "category",
            "supplier",
            "employee",
            "store",
            "creator",
            "approver",
          ],
        },
      });
      return res.data;
    },
  });

  const expenses = useMemo(() => expenseRes?.data || [], [expenseRes?.data]);
  const pagination = {
    total: expenseRes?.meta?.total || expenseRes?.total || expenses.length,
    last_page: expenseRes?.meta?.last_page || 1,
  };

  useEffect(() => {
    if (expenses && expenses.length > 0) {
      const sLabels = {};
      const cLabels = {};
      const eLabels = {};
      expenses.forEach((exp) => {
        if (exp.supplier) {
          sLabels[String(exp.supplier_id)] = exp.supplier.name;
        }
        if (exp.category) {
          cLabels[String(exp.category_id)] = exp.category.name;
        }
        if (exp.employee) {
          eLabels[String(exp.employee_id)] = exp.employee.full_name;
        }
      });

      setSupplierLabels((prev) => ({ ...prev, ...sLabels }));

      setCategoryLabels((prev) => ({ ...prev, ...cLabels }));

      setEmployeeLabels((prev) => ({ ...prev, ...eLabels }));
    }
  }, [expenses]);

  const { data: categories = [], isFetching: isCategoriesFetching } = useQuery({
    queryKey: ["expense-categories", categorySearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/expense-categories`, {
        params: { search: categorySearchTerm || undefined },
      });
      return res.data.data || [];
    },
    enabled: hasPermission("expense category read"),
  });

  useEffect(() => {
    if (categories.length > 0) {
      setCategoryLabels((prev) => {
        const next = { ...prev };
        categories.forEach((c) => {
          next[String(c.id)] = c.name;
        });
        return next;
      });
    }
  }, [categories]);

  const { data: employees = [], isFetching: isEmployeesFetching } = useQuery({
    queryKey: ["employees", employeeSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/employee`, {
        params: { search: employeeSearchTerm || undefined },
      });
      return res.data.data || res.data || [];
    },
    enabled: hasPermission("employee read"),
  });

  useEffect(() => {
    if (employees.length > 0) {
      setEmployeeLabels((prev) => {
        const next = { ...prev };
        employees.forEach((e) => {
          next[String(e.id)] = e.full_name;
        });
        return next;
      });
    }
  }, [employees]);

  const { data: suppliers = [], isFetching: isSuppliersFetching } = useQuery({
    queryKey: ["suppliers", supplierSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/supplier`, {
        params: { search: supplierSearchTerm || undefined },
      });
      return res.data.data || [];
    },
    enabled: hasPermission("supplier read"),
  });

  useEffect(() => {
    if (suppliers.length > 0) {
      setSupplierLabels((prev) => {
        const next = { ...prev };
        suppliers.forEach((s) => {
          next[String(s.id)] = s.name;
        });
        return next;
      });
    }
  }, [suppliers]);

  const { data: stats = null } = useQuery({
    queryKey: ["expenses-stats", store_id],
    queryFn: async () => {
      const res = await apiClient.get(
        `${API_BASE_URL}/expenses/statistics/overview`,
        { params: { store_id: store_id || undefined } },
      );
      return res.data || null;
    },
  });

  // Fetch payment methods available for expenses scope
  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "expenses", store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "expenses",
          store_id: store_id || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
    staleTime: 5 * 60 * 1000,
  });
  const saveExpenseMutation = useMutation({
    mutationFn: async (payload) => {
      const id = payload.id;
      const url = id
        ? `${API_BASE_URL}/expenses/${id}`
        : `${API_BASE_URL}/expenses`;
      const method = id ? "put" : "post";
      return await apiClient({ method, url, data: payload });
    },
    onSuccess: () => {
      toast.success(`Expense ${editMode ? "updated" : "saved"} successfully!`);
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenses-stats"] });
      handleCloseModal();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to save expense");
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(`${API_BASE_URL}/expenses/${id}`);
    },
    onSuccess: () => {
      toast.success("Expense deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenses-stats"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete expense");
    },
  });

  const submitForApprovalMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.post(
        `${API_BASE_URL}/expenses/${id}/submit-for-approval`,
        {},
      );
    },
    onSuccess: () => {
      toast.success("Expense submitted for approval");
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to submit expense for approval",
      );
    },
  });

  const markAsPaidMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.post(
        `${API_BASE_URL}/expenses/${id}/mark-as-paid`,
        {},
      );
    },
    onSuccess: () => {
      toast.success("Expense marked as paid");
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to mark as paid");
    },
  });

  const approveExpenseMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.post(
        `${API_BASE_URL}/expenses/${id}/approve`,
        {},
      );
    },
    onSuccess: () => {
      toast.success("Expense approved");
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to approve expense");
    },
  });

  const rejectExpenseMutation = useMutation({
    mutationFn: async ({ id, reason }) => {
      const payload = {};
      if (reason && reason.trim()) payload.reason = reason.trim();
      return await apiClient.post(
        `${API_BASE_URL}/expenses/${id}/reject`,
        payload,
      );
    },
    onSuccess: () => {
      toast.success("Expense rejected");
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to reject expense");
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount" || name === "tax_amount") {
      setExpenseData((prev) => {
        const updated = { ...prev, [name]: value };
        const amountVal = name === "amount" ? value : prev.amount;
        const taxVal = name === "tax_amount" ? value : prev.tax_amount;

        const a = parseFloat(amountVal);
        const t = parseFloat(taxVal);

        if ((amountVal === "" || isNaN(a)) && (taxVal === "" || isNaN(t))) {
          updated.total_amount = "";
        } else {
          const sum = (isNaN(a) ? 0 : a) + (isNaN(t) ? 0 : t);
          updated.total_amount = sum % 1 === 0 ? String(sum) : sum.toFixed(2);
        }

        return updated;
      });
      return;
    }

    setExpenseData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setExpenseData({
      id: "",
      title: "",
      description: "",
      category_id: "",
      supplier_id: "",
      employee_id: "",
      store_id: "",
      amount: 0,
      tax_amount: 0,
      total_amount: 0,
      expense_date: "",
      payment_method_id: "",
      reference_number: "",
      receipt_number: "",
      notes: "",
      status: "draft",
    });
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleViewDetails = (expense) => {
    setSelectedExpense(expense);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (expense) => {
    const dateVal = expense.expense_date
      ? new Date(expense.expense_date).toISOString().slice(0, 16)
      : "";
    setExpenseData({
      ...expense,
      expense_date: dateVal,
      payment_method_id: expense.payment_method_id || "",
    });
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = (expense) => {
    setDeleteId(expense.id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    deleteExpenseMutation.mutate(deleteId);
    setIsDeleteDialogOpen(false);
  };

  const handleSubmit = async () => {
    if (!expenseData.title || expenseData.title.trim() === "") {
      toast.error("Title is required");
      return;
    }
    if (expenseData.title && expenseData.title.length > 255) {
      toast.error("Title must be <= 255 characters");
      return;
    }
    const amount = Number(expenseData.amount);
    const total = Number(expenseData.total_amount);
    if (isNaN(amount) || amount < 0) {
      toast.error("Amount must be a number >= 0");
      return;
    }
    if (isNaN(total) || total < 0) {
      toast.error("Total amount must be a number >= 0");
      return;
    }
    if (!expenseData.expense_date) {
      toast.error("Expense date is required");
      return;
    }

    let payload = { ...expenseData };
    if (payload.expense_date && payload.expense_date.length === 16) {
      payload.expense_date = new Date(payload.expense_date).toISOString();
    }

    // Map payment_method_id to payment_method for the API payload
    payload.payment_method = payload.payment_method_id;
    delete payload.payment_method_id;

    payload.status = "draft";
    payload.store_id = store_id;
    saveExpenseMutation.mutate(payload);
  };

  const submitForApproval = async (data) => {
    if (!data) return;
    const status = data.status || data.data?.status;
    if (status !== "draft") return;

    const id = data.id || data.data?.id;
    if (!id) return;

    submitForApprovalMutation.mutate(id);
  };

  const markAsPaid = async (payload) => {
    const id = (payload && (payload.id || payload.data?.id)) || payload;
    if (!id) return;
    markAsPaidMutation.mutate(id);
  };

  const handleApprove = (record) => {
    setApproveRecord(record);
    setIsApproveDialogOpen(true);
  };

  const confirmApprove = () => {
    approveExpenseMutation.mutate(approveRecord.id);
    setIsApproveDialogOpen(false);
  };

  const handleReject = (record) => {
    setRejectRecord(record);
    setRejectReason("");
    setIsRejectDialogOpen(true);
  };

  const confirmReject = () => {
    rejectExpenseMutation.mutate({ id: rejectRecord.id, reason: rejectReason });
    setIsRejectDialogOpen(false);
  };

  const isActionPending = (id) =>
    (submitForApprovalMutation.isPending &&
      submitForApprovalMutation.variables === id) ||
    (markAsPaidMutation.isPending && markAsPaidMutation.variables === id) ||
    (approveExpenseMutation.isPending &&
      approveExpenseMutation.variables === id) ||
    (rejectExpenseMutation.isPending &&
      rejectExpenseMutation.variables?.id === id);

  const columns = useMemo(
    () => [
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => {
          const cat = row.original.category;
          return cat ? (
            <Badge
              variant="outline"
              style={{ borderColor: cat.color, color: cat.color }}
            >
              {cat.name}
            </Badge>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "title",
        header: "Title",
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ getValue }) => getValue() ?? "-",
      },
      {
        accessorKey: "tax_amount",
        header: "Tax",
        cell: ({ getValue }) => getValue() ?? "-",
      },
      {
        accessorKey: "total_amount",
        header: "Total",
        cell: ({ getValue }) => getValue() ?? "-",
      },
      {
        accessorKey: "expense_date",
        header: "Date",
        cell: ({ getValue }) =>
          getValue() ? formatDate(getValue()) : "-",
      },
      {
        accessorKey: "payment_method",
        header: "Payment",
        cell: ({ row }) => {
          const pm = row.original.payment_method;
          return pm && pm.name ? pm.name : "-";
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const v = getValue();
          if (!v) return "-";
          const status = v.toString();
          const mapping = {
            draft: "secondary",
            pending_approval: "warning",
            approved: "default",
            rejected: "destructive",
            paid: "success",
          };
          const variant = mapping[status] || "secondary";
          return (
            <Badge variant={variant} className="capitalize">
              {status.replace(/_/g, " ")}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewDetails(record)}>
                  <Eye className="mr-2 h-4 w-4" /> View Details
                </DropdownMenuItem>
                {record.status === "pending_approval" ? (
                  <>
                    {hasPermission("expense approve") && (
                      <DropdownMenuItem
                        onClick={() => handleApprove(record)}
                        disabled={isActionPending(record.id)}
                      >
                        <CheckCircle className="mr-2 h-4 w-4 text-success" />{" "}
                        Approve
                      </DropdownMenuItem>
                    )}
                    {hasPermission("expense reject") && (
                      <DropdownMenuItem
                        onClick={() => handleReject(record)}
                        disabled={isActionPending(record.id)}
                      >
                        <XCircle className="mr-2 h-4 w-4 text-destructive" />{" "}
                        Reject
                      </DropdownMenuItem>
                    )}
                  </>
                ) : (
                  <>
                    {record.status === "approved" &&
                      hasPermission("expense mark_as_paid") && (
                        <DropdownMenuItem
                          onClick={() => markAsPaid(record)}
                          disabled={isActionPending(record.id)}
                        >
                          <DollarSign className="mr-2 h-4 w-4 text-primary" />{" "}
                          Mark as Paid
                        </DropdownMenuItem>
                      )}
                    {record.status === "draft" &&
                      hasPermission("expense submit_for_approval") && (
                        <DropdownMenuItem
                          onClick={() => submitForApproval(record)}
                          disabled={isActionPending(record.id)}
                        >
                          <Send className="mr-2 h-4 w-4 text-primary" /> Send
                          for Approval
                        </DropdownMenuItem>
                      )}
                    {(record.status === "draft" ||
                      record.status === "rejected") &&
                      hasPermission("expense update") && (
                        <DropdownMenuItem onClick={() => handleEdit(record)}>
                          <Edit className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                      )}
                    {hasPermission("expense delete") && (
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleDelete(record)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isActionPending],
  );

  if (!hasPermission("expense read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Expenses"
          subtitle="Manage organization expenses"
        />
        <PageHeaderActions>
          {hasPermission("expense create") && (
            <Button onClick={handleAdd}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Expense
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Statistics Overview */}
      {stats && hasPermission("expense statistics") && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Expenses
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp
                  start={0}
                  end={stats.total_expenses || 0}
                  duration={1.5}
                  prefix={currencySymbol}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Month</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp
                  start={0}
                  end={stats.total_this_month || 0}
                  duration={1.5}
                  prefix={currencySymbol}
                />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Approval
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp
                  start={0}
                  end={stats.pending_approval || 0}
                  duration={1.5}
                />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Approved (Unpaid)
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <CountUp
                  start={0}
                  end={stats.approved_unpaid || 0}
                  duration={1.5}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <FilterCard>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
          <FilterSearch
            placeholder="Search expenses..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <ReactSelect
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            value={
              filterCategory
                ? {
                  value: filterCategory,
                  label:
                    categoryLabels[String(filterCategory)] ||
                    categories.find((c) => c.id === filterCategory)?.name,
                }
                : null
            }
            onChange={(val) => setFilterCategory(val?.value || "")}
            isClearable
            placeholder="Category"
            onInputChange={(val) => setCategorySearchInput(val)}
            isLoading={isCategoriesFetching}
          />
          <ReactSelect
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            value={
              filterSupplier
                ? {
                  value: filterSupplier,
                  label:
                    supplierLabels[String(filterSupplier)] ||
                    suppliers.find((s) => s.id === filterSupplier)?.name,
                }
                : null
            }
            onChange={(val) => setFilterSupplier(val?.value || "")}
            isClearable
            placeholder="Supplier"
            onInputChange={(val) => setSupplierSearchInput(val)}
            isLoading={isSuppliersFetching}
          />
          <ReactSelect
            options={employees.map((e) => ({
              value: e.id,
              label: e.full_name,
            }))}
            value={
              filterEmployee
                ? {
                  value: filterEmployee,
                  label:
                    employeeLabels[String(filterEmployee)] ||
                    employees.find((e) => e.id === filterEmployee)?.full_name,
                }
                : null
            }
            onChange={(val) => setFilterEmployee(val?.value || "")}
            isClearable
            placeholder="Employee"
            onInputChange={(val) => setEmployeeSearchInput(val)}
            isLoading={isEmployeesFetching}
          />
          <ReactSelect
            options={[
              { value: "draft", label: "Draft" },
              { value: "pending_approval", label: "Pending Approval" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "paid", label: "Paid" },
            ]}
            value={
              filterStatus
                ? {
                  value: filterStatus,
                  label: filterStatus
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase()),
                }
                : null
            }
            onChange={(val) => setFilterStatus(val?.value || "")}
            isClearable
            placeholder="Status"
          />
          <div className="flex gap-2">
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              placeholder="From Date"
              title="From Date"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              placeholder="To Date"
              title="To Date"
            />
          </div>
          <div className="flex gap-2 col-span-1 md:col-span-2 xl:col-span-2">
            <ReactSelect
              options={[
                { value: "expense_date", label: "Date" },
                { value: "total_amount", label: "Amount" },
                { value: "title", label: "Title" },
                { value: "created_at", label: "Created At" },
              ]}
              value={
                sortBy
                  ? {
                    value: sortBy,
                    label: sortBy
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                  }
                  : null
              }
              onChange={(val) => setSortBy(val?.value || "")}
              isClearable
              placeholder="Sort By"
              className="flex-1"
            />
            <ReactSelect
              options={[
                { value: "asc", label: "ASC" },
                { value: "desc", label: "DESC" },
              ]}
              value={
                sortOrder
                  ? { value: sortOrder, label: sortOrder.toUpperCase() }
                  : null
              }
              onChange={(val) => setSortOrder(val?.value || "")}
              isClearable
              placeholder="Order"
              className="w-32"
            />
          </div>
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={expenses}
          isLoading={isExpensesLoading}
          enableRowSelection={true}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onRowClick={handleViewDetails}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize: pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            } else {
              setCurrentPage(updater.pageIndex + 1);
              setPageSize(updater.pageSize);
            }
          }}
          pageCount={pagination.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Expenses: {pagination.total || 0}
            </span>
          }
        />
      </TableCard>

      <ExpenseDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={setIsDetailsModalOpen}
        expense={selectedExpense}
      />

      {/* Add/Edit Expense Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Edit Expense" : "Add New Expense"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                name="title"
                value={expenseData.title}
                onChange={handleInputChange}
                placeholder="Enter title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">
                Amount <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                id="amount"
                name="amount"
                value={expenseData.amount || 0}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax_amount">Tax Amount</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                id="tax_amount"
                name="tax_amount"
                value={expenseData.tax_amount || 0}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="total_amount">Total Amount</Label>
              <Input
                type="number"
                id="total_amount"
                value={expenseData.total_amount || 0}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense_date">
                Expense Date <span className="text-destructive">*</span>
              </Label>
              <Input
                type="datetime-local"
                id="expense_date"
                name="expense_date"
                value={expenseData.expense_date}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category_id">Category</Label>
              <ReactSelect
                onInputChange={(value) => setCategorySearchInput(value)}
                options={categories.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                value={
                  expenseData.category_id
                    ? {
                      value: expenseData.category_id,
                      label:
                        categoryLabels[String(expenseData.category_id)] ||
                        categories.find(
                          (c) => c.id == expenseData.category_id,
                        )?.name ||
                        "Select Category",
                    }
                    : null
                }
                onChange={(opt) =>
                  setExpenseData({ ...expenseData, category_id: opt?.value })
                }
                placeholder="Search Category..."
                isLoading={isCategoriesFetching}
                isClearable
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier_id">Supplier</Label>
              <ReactSelect
                onInputChange={(value) => setSupplierSearchInput(value)}
                options={suppliers.map((s) => ({
                  value: String(s.id),
                  label: s.name,
                }))}
                value={
                  expenseData.supplier_id
                    ? {
                      value: String(expenseData.supplier_id),
                      label:
                        supplierLabels[String(expenseData.supplier_id)] ||
                        suppliers.find(
                          (s) =>
                            String(s.id) === String(expenseData.supplier_id),
                        )?.name ||
                        "Select Supplier",
                    }
                    : null
                }
                onChange={(opt) =>
                  setExpenseData({
                    ...expenseData,
                    supplier_id: opt ? opt.value : "",
                  })
                }
                placeholder="Search Supplier..."
                isLoading={isSuppliersFetching}
                isClearable
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_method">Payment Method</Label>
              <ReactSelect
                options={availablePaymentMethods.map((m) => ({
                  value: m.id ?? m.code ?? m,
                  label: m.name ?? String(m.code ?? m).replace(/_/g, " "),
                }))}
                value={
                  expenseData.payment_method_id
                    ? (() => {
                      const match = availablePaymentMethods.find(
                        (m) =>
                          (m.id ?? m.code ?? m) ===
                          expenseData.payment_method_id,
                      );
                      return {
                        value: expenseData.payment_method_id,
                        label: match
                          ? match.name ??
                          String(match.code ?? match).replace(/_/g, " ")
                          : "Unknown Payment Method",
                      };
                    })()
                    : null
                }
                onChange={(opt) =>
                  setExpenseData({
                    ...expenseData,
                    payment_method_id: opt?.value ?? null,
                  })
                }
                isClearable
                placeholder="Select payment method…"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference_number">Reference Number</Label>
              <Input
                type="text"
                id="reference_number"
                name="reference_number"
                value={expenseData.reference_number}
                onChange={handleInputChange}
                placeholder="E.g. Invoice #"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receipt_number">Receipt Number</Label>
              <Input
                type="text"
                id="receipt_number"
                name="receipt_number"
                value={expenseData.receipt_number}
                onChange={handleInputChange}
                placeholder="Receipt #"
              />
            </div>
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Label htmlFor="description">Description</Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <DefaultEditor
                  id="description"
                  value={expenseData.description}
                  onChange={(e) =>
                    setExpenseData({
                      ...expenseData,
                      description: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <DefaultEditor
                  id="notes"
                  value={expenseData.notes}
                  onChange={(e) =>
                    setExpenseData({ ...expenseData, notes: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saveExpenseMutation.isPending}
            >
              {saveExpenseMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              expense record and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isApproveDialogOpen}
        onOpenChange={setIsApproveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Expense?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the expense as approved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmApprove}>
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Expense?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for rejection (optional)</Label>
              <Textarea
                placeholder="Enter reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRejectDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmReject}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default Expenses;
